//#region header */
/**************************************************************************************************
//
//  Description: Organisation Tree component to display the list of organisations in a tree view.
//
//  Copyright:    © 2026 Idox Software Limited
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//#region Version 1.0.0.0 changes
//    001   05.02.26 Sean Flook         GMSCM-12 Initial Revision.
//#endregion Version 1.0.0.0 changes
//
//--------------------------------------------------------------------------------------------------
//#endregion header */

import React, { ChangeEvent, Fragment, SyntheticEvent, useEffect, useState } from "react";

import useUserQueryStore from "../context/UserStore";

import { Box, IconButton, InputAdornment, Menu, MenuItem, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";

import { ExpandMore, ChevronRight } from "@mui/icons-material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ClearIcon from "@mui/icons-material/Clear";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";

import {
  ActionIconStyle,
  ClearSearchIconStyle,
  dataFormStyle,
  filterToolbarStyle,
  FormInputStyle,
  menuItemStyle,
  menuStyle,
  toolbarStyle,
  tooltipStyle,
} from "../utils/ADSStyles";
import { useTheme } from "@mui/material/styles";
import {
  adsBlueA,
  adsDarkRed,
  adsLightGreyB,
  adsPaleBlueA,
  adsRed,
  adsRed10,
  adsRed20,
} from "../utils/ADSColour";
import OrganisationGridType from "../models/organisationGridType";

interface OrganisationTreeProps {
  data: OrganisationGridType[];
  selectedItem: number | undefined;
  expanded: string[];
  haveErrors: boolean;
  errorIds: number[];
  onItemSelected: (id: number | undefined) => void;
  onAddOrganisation: () => void;
  onExpandedItemsChange: (nodeIds: string[]) => void;
}

const OrganisationTree: React.FC<OrganisationTreeProps> = ({
  data,
  selectedItem,
  expanded,
  haveErrors,
  errorIds,
  onItemSelected,
  onAddOrganisation,
  onExpandedItemsChange,
}) => {
  const theme = useTheme();

  const currentUser = useUserQueryStore((s) => s.userQuery.currentUser);

  const [filterBy, setFilterBy] = useState<string>("");
  const [filteredData, setFilteredData] = useState<OrganisationGridType[]>(data);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  /**
   * Event to handle when the organisation menu is clicked.
   *
   * @param event The event object.
   */
  const handleOrganisationMenuClick = (event: SyntheticEvent<HTMLElement | null>) => {
    setAnchorEl(event.nativeEvent.target as HTMLElement);
  };

  /**
   * Event to handle when the organisation menu is closed.
   *
   * @param event The event object.
   */
  const handleOrganisationMenuClose = (event: SyntheticEvent) => {
    setAnchorEl(null);
    event.stopPropagation();
  };

  /**
   * Event to handle when the add organisation menu item is clicked.
   *
   * @param event The event object.
   */
  const handleAddOrganisationClick = (event: SyntheticEvent): void => {
    handleOrganisationMenuClose(event);
    if (onAddOrganisation) onAddOrganisation();
  };

  /**
   * Event to handle when the filter changes.
   *
   * @param newValue The new value to filter by.
   */
  const handleFilterChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const newValue = event.target.value;
    setFilterBy(newValue);
    if (newValue.length > 0) {
      const filteredList: OrganisationGridType[] = data.filter((org) =>
        org.organisation.toLowerCase().includes(newValue.toLowerCase())
      );
      setFilteredData(filteredList);
      const foundCurrent = filteredList.find((org) => org.id === selectedItem);
      if (!foundCurrent && onItemSelected) onItemSelected(undefined);
    } else {
      setFilteredData(data);
    }
  };

  /**
   * Event to handle when the clear search button is clicked.
   */
  const handleClearSearch = (): void => {
    setFilterBy("");
    setFilteredData(data);
  };

  /**
   * Event to handle when an organisation is selected.
   *
   * @param {object} event The event object.
   * @param {string} itemIds The id of the selected organisation.
   */
  function handleOrganisationSelected(event: SyntheticEvent, itemIds: string | null) {
    event.stopPropagation();
    if (onItemSelected) onItemSelected(Number(itemIds) ?? undefined);
  }

  /**
   * Event to handle the toggling of the organisations.
   *
   * @param {object} event The event object.
   * @param {array} organisationNames The list of organisation names that are expanded.
   */
  function handleOrganisationToggle(event: SyntheticEvent, organisationNames: string[]): void {
    event.stopPropagation();
    const iconClicked = (event.target as HTMLElement).closest(".MuiTreeItem-iconContainer");
    if (iconClicked && onExpandedItemsChange) {
      onExpandedItemsChange(organisationNames);
    }
  }

  /**
   * Method to get styling used for a tree item, based on whether usrn is current street and/or checked.
   *
   * @param {string} id The id for the document
   * @returns {object} The styling for the tree item.
   */
  const treeItemStyle = (id: number, hasError: boolean): object => {
    const currentOrganisation = id === selectedItem;
    if (hasError)
      return {
        backgroundColor: adsRed10,
        color: adsRed,
        borderTop: `solid ${adsLightGreyB} 1px`,
        pb: theme.spacing(1),
        pt: theme.spacing(1),
        "&:hover": {
          backgroundColor: adsRed20,
          color: adsDarkRed,
        },
      };
    else if (currentOrganisation)
      return {
        backgroundColor: adsPaleBlueA,
        borderTop: `solid ${adsLightGreyB} 1px`,
        pb: theme.spacing(1),
        pt: theme.spacing(1),
        "&:hover": {
          backgroundColor: adsPaleBlueA,
          color: adsBlueA,
        },
      };
    else
      return {
        backgroundColor: "inherit",
        borderTop: `solid ${adsLightGreyB} 1px`,
        pb: theme.spacing(1),
        pt: theme.spacing(1),
        "&:hover": {
          backgroundColor: adsPaleBlueA,
          color: adsBlueA,
        },
      };
  };

  useEffect(() => {
    if (data && data.length > 0) {
      setFilteredData(data);
    } else {
      setFilteredData([]);
    }
  }, [data]);

  return (
    <Fragment>
      <Box sx={toolbarStyle} id="ads-organisation-tree-toolbar">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ pl: "12px", pr: "12px", pt: "4px" }}
        >
          <Typography variant="body2">{`${
            data.length === filteredData.length
              ? data.length
              : filteredData.length.toString() + " of " + data.length.toString()
          } organisations`}</Typography>
          <Stack direction="row" alignItems="center" justifyContent={"flex-end"} spacing={1}>
            <Tooltip title="Actions" arrow placement="right" sx={tooltipStyle}>
              <IconButton
                onClick={handleOrganisationMenuClick}
                aria-controls="organisation_action-menu"
                aria-haspopup="true"
                size="small"
              >
                <MoreVertIcon sx={ActionIconStyle()} />
              </IconButton>
            </Tooltip>
            <Menu
              id="organisation_action-menu"
              elevation={2}
              anchorEl={anchorEl}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleOrganisationMenuClose}
              sx={menuStyle}
            >
              {currentUser?.isSuperAdministrator && (
                <MenuItem dense onClick={handleAddOrganisationClick} sx={menuItemStyle(false)}>
                  <Typography variant="inherit">Add organisation</Typography>
                </MenuItem>
              )}
            </Menu>
          </Stack>
        </Stack>
      </Box>
      <Box sx={filterToolbarStyle} id="ads-organisation-tree-filter-toolbar">
        <TextField
          id="organisation-filter"
          label="Filter by organisation name"
          sx={FormInputStyle(false)}
          variant="outlined"
          fullWidth
          margin="dense"
          size="small"
          value={filterBy}
          onChange={handleFilterChange}
          placeholder="Filter by organisation name"
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton id="btnClear" onClick={handleClearSearch} aria-label="Clear filter" size="small">
                    <ClearIcon sx={ClearSearchIconStyle(filterBy)} />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
      <Box sx={dataFormStyle("OrganisationTree")}>
        <SimpleTreeView
          aria-label="organisation tree"
          id="organisation-tree"
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            "& .MuiTreeItem-content": {
              borderRadius: 0,
              margin: 0,
              backgroundColor: "transparent !important",
              boxShadow: "none !important",
            },
            "& .MuiTreeItem-content:hover": {
              backgroundColor: "transparent !important",
            },
            "& .MuiTreeItem-content.Mui-focused, & .MuiTreeItem-content.Mui-selected, & .MuiTreeItem-content.Mui-selected:hover":
              {
                backgroundColor: "transparent !important",
                boxShadow: "none !important",
              },
            "& .MuiTreeItem-label": {
              backgroundColor: "transparent !important",
            },
          }}
          slots={{ expandIcon: ChevronRight, collapseIcon: ExpandMore }}
          expandedItems={expanded}
          selectedItems={selectedItem?.toString()}
          onSelectedItemsChange={handleOrganisationSelected}
          onExpandedItemsChange={handleOrganisationToggle}
        >
          {filteredData
            .sort((a, b) =>
              a.organisation.localeCompare(b.organisation, undefined, { numeric: true, sensitivity: "base" })
            )
            .map((rec, index) => {
              return (
                <TreeItem
                  key={`organisation-${rec.organisation}-${index}`}
                  itemId={`${rec.id}`}
                  sx={treeItemStyle(rec.id, haveErrors && errorIds.includes(rec.id))}
                  label={
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                      <Stack direction="column" alignItems="flex-start" justifyContent="flex-start" spacing={1}>
                        <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={1}>
                          <CorporateFareIcon />
                          <Typography variant="subtitle2">{rec.organisation}</Typography>
                        </Stack>
                      </Stack>
                    </Stack>
                  }
                />
              );
            })}
        </SimpleTreeView>
      </Box>
    </Fragment>
  );
};

export default OrganisationTree;
